import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { PageSeoHead } from '@/components/seo/PageSeoHead';
import { useCollections } from '@/hooks/useProducts';

import collectionMountain from '@/assets/collection-mountain.jpg';
import collectionFauna from '@/assets/collection-fauna-page.jpg';
import collectionEquestrian from '@/assets/collection-equestrian-page.jpg';

// Map Shopify collection handles to URL slugs and fallback images
const handleToSlug: Record<string, { slug: string; image: string }> = {
  montagna: { slug: 'mountain', image: collectionMountain },
  fauna: { slug: 'fauna', image: collectionFauna },
  equitazione: { slug: 'equestrian', image: collectionEquestrian },
};

// Ordered keys to control display order
const displayOrder = ['montagna', 'fauna', 'equitazione'];

const Collections = () => {
  const { t, i18n } = useTranslation();
  const isIT = i18n.language === 'it';
  const { data: shopifyCollections, isLoading } = useCollections();

  // Filter and sort collections based on our displayOrder
  const collections = displayOrder
    .map(handle => {
      const shopify = shopifyCollections?.find(c => c.handle === handle);
      const mapping = handleToSlug[handle];
      if (!mapping) return null;
      return {
        handle,
        slug: mapping.slug,
        image: mapping.image,
        title: shopify?.title || t(`collections.${mapping.slug}.name`),
        description: shopify?.description || t(`collections.${mapping.slug}.description`),
      };
    })
    .filter(Boolean) as Array<{ handle: string; slug: string; image: string; title: string; description: string }>;

  return (
    <Layout>
      <PageSeoHead
        title={isIT
          ? 'Collezioni Gioielli Artigianali in Bronzo | Sampaoli Creazioni'
          : 'Handcrafted Bronze Jewelry Collections | Sampaoli'}
        description={isIT
          ? 'Esplora le collezioni Montagna, Fauna e Equestre di Sampaoli. Gioielli artigianali in bronzo fatti a mano in Italia. Spille, braccialetti, collane e cinture.'
          : 'Explore Sampaoli Mountain, Wildlife & Equestrian collections. Handcrafted bronze jewelry made in Italy. Pins, bracelets, necklaces and belts.'}
        path="/collections"
      />
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="container-wide text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4">
              {t('collections.title')}
            </h1>
            <p className="font-elegant text-xl text-muted-foreground italic max-w-2xl mx-auto">
              {t('collections.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Collections List */}
      <section className="py-12 md:py-20 bg-background">
        <div className="container mx-auto px-4 max-w-6xl">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-bronze" />
            </div>
          ) : (
            <div className="space-y-16 md:space-y-24">
              {collections.map((collection, index) => (
                <motion.div
                  key={collection.handle}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center ${
                    index % 2 === 1 ? 'md:flex-row-reverse' : ''
                  }`}
                >
                  {/* Image */}
                  <Link 
                    to={`/collections/${collection.slug}`}
                    className={`relative overflow-hidden rounded-lg aspect-[4/3] group ${
                      index % 2 === 1 ? 'md:order-2' : ''
                    }`}
                  >
                    <img
                      src={collection.image}
                      alt={collection.title}
                      className="absolute inset-0 w-full h-full object-cover img-zoom"
                      style={collection.handle === 'fauna' ? { objectPosition: '50% 15%' } : undefined}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6">
                      <h3 className="font-heading text-xl md:text-2xl font-semibold text-ivory mb-2">
                        {collection.title}
                      </h3>
                      <p className="font-body text-sm text-cream/80 line-clamp-2">
                        {collection.description}
                      </p>
                      <span className="inline-flex items-center gap-1 text-bronze font-medium text-sm mt-3 group-hover:gap-2 transition-all">
                        {t('collections.viewCollection')}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>

                  {/* Text Content */}
                  <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                    <span className="font-body text-xs uppercase tracking-[0.2em] text-bronze mb-3 block">
                      {t('collections.label')} {index + 1}
                    </span>
                    <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-4">
                      {collection.title}
                    </h2>
                    <p className="font-body text-muted-foreground leading-relaxed mb-6">
                      {collection.description}
                    </p>
                    <Link
                      to={`/collections/${collection.slug}`}
                      className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
                    >
                      {t('collections.viewCollection')}
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Collections;
